import apiClient from "@/lib/axios";

// Customer API service for all customer-specific operations
export const customerApi = {
  // Get customer by ID (for agents to view their leads)
  getCustomerById: async (customerId: string) => {
    const response = await apiClient.get(`/api/customer/${customerId}`);
    return response.data.data;
  },

  // Dashboard
  getDashboard: async () => {
    const response = await apiClient.get("/api/customers/dashboard");
    return response.data.data;
  },

  // Profile operations (preferences, notifications, avatar)
  updatePreferences: async (preferences: any) => {
    const response = await apiClient.put(
      "/api/customers/preferences",
      preferences
    );
    return response.data.data;
  },

  updateNotificationSettings: async (notifications: any) => {
    const response = await apiClient.put(
      "/api/customers/notifications",
      notifications
    );
    return response.data.data;
  },

  uploadAvatar: async (avatarUrl: string) => {
    const response = await apiClient.post("/api/customers/avatar", {
      avatarUrl,
    });
    return response.data.data;
  },

  // Bookings
  getBookings: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    sortBy?: string;
    sortOrder?: string;
    filters?: any;
    sortOptions?: any;
  }) => {
    const response = await apiClient.get("/api/customers/bookings", { params });
    return response.data.data;
  },

  createBooking: async (bookingData: {
    propertyId: string;
    agentId: string;
    date: string;
    time: string;
    duration: number;
    message?: string;
    contactInfo: {
      name: string;
      email: string;
      phone: string;
    };
  }) => {
    const response = await apiClient.post(
      "/api/customers/bookings",
      bookingData
    );
    return response.data.data;
  },

  updateBooking: async (
    bookingId: string,
    updates: { customerNotes?: string }
  ) => {
    const response = await apiClient.put(
      `/api/customers/bookings/${bookingId}`,
      updates
    );
    return response.data.data;
  },

  cancelBooking: async (bookingId: string, reason?: string) => {
    const response = await apiClient.delete(
      `/api/customers/bookings/${bookingId}`,
      {
        data: { reason },
      }
    );
    return response.data.data;
  },

  rescheduleBooking: async (
    bookingId: string,
    rescheduleData: {
      newDate: string;
      newTime: string;
      reason?: string;
    }
  ) => {
    const response = await apiClient.put(
      `/api/customers/bookings/${bookingId}/reschedule`,
      rescheduleData
    );
    return response.data.data;
  },

  // Saved Properties
  getSavedProperties: async (params?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
  }) => {
    const response = await apiClient.get("/api/customers/saved-properties", {
      params,
    });
    return response.data.data;
  },

  saveProperty: async (propertyId: string) => {
    const response = await apiClient.post(
      `/api/customers/saved-properties/${propertyId}`
    );
    return response.data.data;
  },

  unsaveProperty: async (propertyId: string) => {
    const response = await apiClient.delete(
      `/api/customers/saved-properties/${propertyId}`
    );
    return response.data.data;
  },
};

// Generic update customer function for agents to update customer data
export const updateCustomer = async (
  customerId: string,
  data: Record<string, any>
) => {
  const response = await apiClient.patch(`/api/customer/${customerId}`, data);
  return response.data.data;
};
